import React, { useEffect, useRef, useState } from 'react';
import { PopUpModal } from '../../components/molecules/PopUpModal/PopUpModal';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector, useIsMultiPortfolioTenant } from '../../common/hooks';
import { ReactComponent as InfoIconBlue } from '../../assets/icons/info-blue.svg';
import { BasicButton, Spinner, TextboxInput, TermChip } from '../../components/atoms';
import { ConfirmationCentered } from '../../components/molecules/ConfirmationDialog/ConfirmationDialog';
import { TextBoxProps } from '../../components/atoms/TextboxInput/TextboxInput';
import { TextFieldProps } from '@mui/material/TextField';
import { Box, IconButton, styled, SxProps, Theme, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TableWithCustomRender } from '../../components/molecules';
import { StalePlanBanner } from '../../components/molecules/StalePlanBanner/StalePlanBanner';
import {
    formatCurrency,
    removeNonNumeric,
    getDurationLabelFromAccountType,
    getLabelFromAccountType
} from '../../utils';
import { DEFAULT_GOAL_CATEGORY, EWS_DURATION_KEYS, EwsDuration } from '../../common/constants';
import dayjs from 'dayjs';

export const InfoBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gridColumn: 'span 2',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 16px',
    borderRadius: '6px',
    backgroundColor: theme.palette.info[50],
    border: `1px solid ${theme.palette.info[400]}`
}));

type ReallocateAccountBalancesProps = {
    isOpen: boolean;
    onClose: () => void;
    // eslint-disable-next-line no-unused-vars
    onSubmit: (isEdited: boolean, values: any) => void;
    investmentAccounts: any[];
    // AEX-3218: receives the accounts whose balance was edited in the modal, so they are retained. The modal
    // stays open; the parent closes it once it navigates (and keeps it open on a stale plan, for the banner).
    // eslint-disable-next-line no-unused-vars
    onNavigateToProfile?: (editedAccounts: any[]) => void;
    // AEX-3218: the parent is acting on a CTA while the modal stays open. The app's loading overlay renders
    // below dialogs, so the modal shows its own and ignores close requests meanwhile.
    isBusy?: boolean;
    // AEX-3363: when the save was rejected as stale, show the banner + "Refresh Plan" CTA in-modal.
    isStale?: boolean;
    onRefreshPlan?: () => void;
    // AEX-3218: when launched as step 2 of the Review Goals flow, show the "Step 2 of 2" title + a
    // "Back: Review Goals" button. Defaults keep the standalone (console) behaviour unchanged.
    isStepTwo?: boolean;
    onBack?: () => void;
};

type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

const ReallocateAccountBalancesModal: React.FC<ReallocateAccountBalancesProps> = ({
    onClose,
    onSubmit,
    investmentAccounts,
    onNavigateToProfile,
    isBusy = false,
    isStale,
    onRefreshPlan,
    isStepTwo = false,
    onBack
}) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const { investmentsSources } = adviceClient?.accounts || {};
    const { clientFirstName, clientLastName } = adviceClient || {};
    const clientName = `${clientFirstName || ''} ${clientLastName || ''}`.trim();
    const { orgInfo } = useAppSelector((state) => state.global);
    const investmentAccountType = orgInfo?.investmentAccountType?.[language] || [];
    const theme = useTheme();
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const globalData = adviceGoals?.global as any;
    const lastUpdateDate = globalData?.lastUpdateDate ? dayjs(globalData.lastUpdateDate).format('DD MMM YYYY') : '';
    const accountSourcesRef = useRef<any[]>([]);
    const originalAmountsRef = useRef<Map<string, any>>(new Map());
    const changedAccountIdsRef = useRef<Set<string>>(new Set());
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [, forceRender] = useState(0);

    const handleModalClose = () => {
        if (isBusy) return;
        if (checkIfBalancesEdited()) {
            setShowConfirmDialog(true);
        } else {
            onClose();
        }
    };

    const checkIfBalancesEdited = () => {
        if (!investmentsSources) return false;
        return investmentsSources.some((original: any) => {
            const current = accountSourcesRef.current.find((acc: any) => acc.accountId === original.accountId);
            return current && Number(current.amount) !== Number(original.amount);
        });
    };

    useEffect(() => {
        if (investmentsSources && investmentsSources.length > 0) {
            accountSourcesRef.current = investmentsSources;
            originalAmountsRef.current = new Map(investmentsSources.map((src: any) => [src.accountId, src.amount]));
            forceRender((prev) => prev + 1);
        }
    }, [investmentsSources]);

    const checkIsSourceEdited = (original: any[], current: any[]): boolean => {
        const isSameValue = (left: any, right: any): boolean => {
            if (left === right) return true;
            if (Array.isArray(left) && Array.isArray(right)) {
                if (left.length !== right.length) return false;
                return left.every((item, i) => isSameValue(item, right[i]));
            }
            if (left && right && typeof left === 'object' && typeof right === 'object') {
                const leftKeys = Object.keys(left);
                const rightKeys = Object.keys(right);
                if (leftKeys.length !== rightKeys.length) return false;
                return leftKeys.every(
                    (key) => Object.prototype.hasOwnProperty.call(right, key) && isSameValue(left[key], right[key])
                );
            }
            return false;
        };
        if (original.length !== current.length) return true;
        return original.some((item, i) => !isSameValue(item, current[i]));
    };

    const bodyText = {
        fontFamily: '"TT Commons Pro"',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '22px',
        fontFeatureSettings: '"liga" 0',
        color: theme.palette.semantic.text.default
    };
    const headerCellSx = { padding: '8px', verticalAlign: 'middle', '&&': { height: '40px' } };
    const textCellSx = { padding: '16px 8px 12px', verticalAlign: 'top' };

    const accountHeaders: {
        label: string;
        key: string;
        align: ColumnAlign;
        width: string;
        headerSx?: SxProps<Theme>;
        cellSx?: SxProps<Theme>;
        // eslint-disable-next-line no-unused-vars
        render?: (value: any, row: any) => React.ReactNode;
    }[] = [
        {
            label: t('TEXT_ACCOUNT_NAME'),
            key: 'name',
            align: 'left',
            width: '28%',
            headerSx: headerCellSx,
            cellSx: textCellSx,
            render: (_value: any, row: any) => row['accountName']
        },
        {
            label: t('OWNER'),
            key: 'accountHolder',
            align: 'left',
            width: '10%',
            headerSx: headerCellSx,
            cellSx: textCellSx,
            render: (value: string) =>
                value === 'primary' ? t('CLIENT') : value === 'joint' ? t('TEXT_JOINT') : t('CO-CLIENT')
        },
        {
            label: t('TYPE'),
            key: 'type',
            align: 'left',
            width: '22%',
            headerSx: headerCellSx,
            cellSx: textCellSx,
            render: (_value: any, row: any) => {
                const duration = getDurationLabelFromAccountType(row.accountType, investmentAccountType);
                const accountTypeLabel = getLabelFromAccountType(row.accountType, investmentAccountType);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        {isMultiPortfolioTenant && (
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    '& .MuiChip-root': { height: '28px', width: '98px' }
                                }}
                            >
                                <TermChip term={duration} variant="bordered" />
                            </Box>
                        )}
                        <Box component="span" sx={bodyText}>
                            {accountTypeLabel}
                        </Box>
                    </Box>
                );
            }
        },
        {
            label: t('LAST_UPDATED_BALANCE'),
            key: 'lastUpdatedAmount',
            align: 'right' as const,
            width: '19%',
            headerSx: headerCellSx,
            cellSx: textCellSx,
            render: (_value: any, row: any) =>
                formatCurrency(Number(removeNonNumeric(originalAmountsRef.current.get(row.accountId))), true)
        },
        {
            label: t('CURRENT_BALANCE'),
            key: 'amount',
            align: 'right' as const,
            width: '19%',
            headerSx: headerCellSx,
            cellSx: { padding: '12px 8px', verticalAlign: 'top' },
            render: (value: any, row: any) => {
                return (
                    <TextRowInput
                        onChange={(value: number | string) => {
                            accountSourcesRef.current = accountSourcesRef.current.map((acc) =>
                                acc.accountId === row.accountId ? { ...acc, amount: value } : acc
                            );
                            changedAccountIdsRef.current.add(row.accountId);
                        }}
                        value={value}
                        size="small"
                        type="text"
                        fullWidth
                        sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root:hover': {
                                height: '32px',
                                padding: '0 16px',
                                borderRadius: '4px',
                                borderColor: theme.palette.neutral[500]
                            },
                            '& .MuiInputBase-input': {
                                ...bodyText,
                                height: '22px',
                                padding: 0,
                                textAlign: 'right'
                            }
                        }}
                    />
                );
            }
        }
    ];

    // AEX-3218: shared by both footer variants (step 2 of Review Goals + standalone).
    const handleReallocateClick = () => {
        const affectedEwsTerms = Array.from(
            new Set(
                accountSourcesRef.current
                    .filter((acc) => changedAccountIdsRef.current.has(acc.accountId))
                    .map((acc) => {
                        if (!isMultiPortfolioTenant) return DEFAULT_GOAL_CATEGORY;
                        const duration = getDurationLabelFromAccountType(acc.accountType, investmentAccountType);
                        return EWS_DURATION_KEYS[duration as EwsDuration];
                    })
                    .filter(Boolean)
            )
        );
        onSubmit(checkIsSourceEdited(investmentAccounts, accountSourcesRef.current), {
            accounts: accountSourcesRef.current,
            affectedEwsTerms
        });
    };

    // AEX-3218: like Reallocate, the Profile & Finances link carries over the balances edited so far — the
    // accounts whose balance differs from the one the modal opened with.
    const handleProfileLinkClick = () => {
        if (!onNavigateToProfile) {
            onClose();
            return;
        }
        const toAmount = (value: any) => Number(removeNonNumeric(value ?? 0) || 0);
        const editedAccounts = accountSourcesRef.current.filter(
            (account) => toAmount(account.amount) !== toAmount(originalAmountsRef.current.get(account.accountId))
        );
        onNavigateToProfile(editedAccounts);
    };

    return (
        <>
            <PopUpModal
                open={true}
                onClose={handleModalClose}
                titleBold
                title={
                    isStepTwo
                        ? t('REVIEW_GOALS_STEP2_TITLE', { clientName: clientName })
                        : t('REALLOCATE_PLAN_FOR', { clientName: clientName })
                }
                width="1300px"
                height="85vh"
                transitionDuration={isStepTwo ? 0 : undefined}
                handleClose={handleModalClose}
                handleSaveButtonAction={function (): void {
                    console.log('Function not implemented.');
                }}
                saveButtonLabel={t('CLIENT_ONBOARDING_CONTD_BTN')}
                showTitle={!isStepTwo}
                subTitle={t('REALLOCATE_POPUP_SUBTITLE', { last_update_date: lastUpdateDate })}
                showCancelButton={false}
                sx={
                    isStepTwo
                        ? {
                              '& .MuiDialogContent-root': {
                                  padding: '32px',
                                  borderTop: 'none',
                                  borderBottom: 'none'
                              },
                              '& .MuiDialogActions-root': { padding: '0 32px 32px' }
                          }
                        : undefined
                }
                description={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                        <Spinner enabled={isBusy} />
                        {isStepTwo && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            flex: '1 1 0',
                                            fontFamily: '"TT Commons Pro"',
                                            fontWeight: 700,
                                            fontSize: '24px',
                                            lineHeight: '30px',
                                            color: theme.palette.primary[800]
                                        }}
                                    >
                                        {t('REVIEW_GOALS_STEP2_TITLE', { clientName: clientName })}
                                    </Box>
                                    <IconButton
                                        aria-label="Close"
                                        onClick={handleModalClose}
                                        sx={{ padding: '7px', color: theme.palette.semantic.icon.dark }}
                                    >
                                        <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                                <Box component="span" sx={bodyText}>
                                    {t('REALLOCATE_POPUP_SUBTITLE', { last_update_date: lastUpdateDate })}
                                </Box>
                            </Box>
                        )}
                        {isStale && onRefreshPlan && <StalePlanBanner onRefresh={onRefreshPlan} />}
                        <InfoBox>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    width: '24px',
                                    height: '24px',
                                    '& svg': { width: '20px', height: '20px' }
                                }}
                            >
                                <InfoIconBlue aria-hidden />
                            </Box>
                            <Box component="span" sx={bodyText}>
                                <Trans
                                    i18nKey={'UPDATE_ACCOUNT_BALANCES_INFO_TEXT'}
                                    components={{
                                        bold: <Box component="span" sx={{ fontWeight: 700 }} />,
                                        profileLink: (
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleProfileLinkClick();
                                                }}
                                                style={{
                                                    fontWeight: 600,
                                                    lineHeight: '20px',
                                                    textDecoration: 'underline',
                                                    color: theme.palette.semantic.int.selected,
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </Box>
                        </InfoBox>
                        <Box
                            sx={{
                                border: `1px solid ${theme.palette.neutral[200]}`,
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        >
                            <TableWithCustomRender
                                headers={accountHeaders}
                                dataSource={accountSourcesRef.current}
                                variant="ftds"
                                embedded="all"
                                rowHeight="40px"
                            />
                        </Box>
                    </Box>
                }
                footerComp={
                    isStepTwo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '16px' }}>
                            <Box sx={{ flex: '1 1 0' }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleModalClose}
                                    sx={{
                                        background: 'transparent',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        ...bodyText,
                                        whiteSpace: 'nowrap',
                                        color: theme.palette.semantic.int.selected,
                                        textDecorationLine: 'underline',
                                        textDecorationSkipInk: 'none',
                                        textUnderlinePosition: 'from-font'
                                    }}
                                >
                                    {t('BUTTON_CANCEL')}
                                </Box>
                            </Box>
                            {onBack && (
                                <BasicButton onClick={onBack} variant="outlined">
                                    {t('REVIEW_GOALS_BACK')}
                                </BasicButton>
                            )}
                            <BasicButton onClick={handleReallocateClick} variant="contained">
                                {t('REALLOCATE_PLAN')}
                            </BasicButton>
                        </Box>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    gap: '12px',
                                    padding: '16px',
                                    width: '50%'
                                }}
                            >
                                <BasicButton onClick={handleModalClose} variant="outlined">
                                    {t('BUTTON_CANCEL')}
                                </BasicButton>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px',
                                    padding: '16px',
                                    width: '50%'
                                }}
                            >
                                <BasicButton onClick={handleReallocateClick} variant="contained">
                                    {t('REALLOCATE_PLAN')}
                                </BasicButton>
                            </Box>
                        </>
                    )
                }
            ></PopUpModal>
            <Box sx={{ position: 'relative', zIndex: 1400 }}>
                <ConfirmationCentered
                    onCancellation={() => {
                        setShowConfirmDialog(false);
                        onClose();
                    }}
                    onConfirmation={() => setShowConfirmDialog(false)}
                    open={showConfirmDialog}
                    content={t('LEAVE_WITHOUT_UPDATING_BALANCES_CONTENT')}
                    confirmButtonLabel={t('CONSOLE_NAVIGATION_CONFIRMATION_STAY')}
                    cancelButtonLabel={t('LEAVE_ANYWAY')}
                    title={t('LEAVE_WITHOUT_UPDATING_BALANCES_TITLE')}
                    width="100vw"
                    cardMaxWidth="350px"
                    onCloseIconClick={() => setShowConfirmDialog(false)}
                    cancelButtonFirst
                />
            </Box>
        </>
    );
};

const TextRowInput: React.FC<
    // eslint-disable-next-line no-unused-vars
    Omit<TextBoxProps & TextFieldProps, 'onChange'> & { onChange?: (value: number | string) => void }
> = ({ value, onChange, size = 'medium', type = 'text', fullWidth = false, iconPlacement, sx = {}, ...otherProps }) => {
    const [inputValue, setInputValue] = useState(Number(removeNonNumeric(value)));

    return (
        <TextboxInput
            value={formatCurrency(inputValue, true)}
            onChange={(e) => {
                let numericValue = Number(removeNonNumeric(e.target.value));
                setInputValue(numericValue);
                onChange?.(numericValue);
            }}
            size={size}
            type={type}
            fullWidth={fullWidth}
            iconPlacement={iconPlacement}
            sx={sx}
            {...otherProps}
        />
    );
};

export default ReallocateAccountBalancesModal;
